import { defineStore } from "pinia";

import { useUserStore } from "./user";
import { useNotificationStore } from './notification';

import { setKey as setBrowserStorageKey } from "./helpers/localStorage.mjs";
import { fetchFromBackend } from "./helpers/backend.mjs";


export const useIncentivesStore = defineStore("incentives", {
  state: () => {
    return {
      incentives: [],
    }
  },
  getters: {
    // unify resolving team id (backend may deliver idSalesTeam instead of idTeam)
    _teamId() { return obj => obj?.idTeam ?? obj?.idSalesTeam ?? null; },
    /**
     * Internal helper getter returning the currently selected incentive object
     * (raw shape from backend) or null if none is cached yet.
     */
    _current(state) {
      const userStore = useUserStore();
      return state.incentives.find((i) => i.signature === `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`) || null;
    },
    // return the full incentive data, the signature allows for finding the incentive by display (i.e. salesAgens or salesTeams) and campaign
    incentive(state) {
      const inc = this._current;
      if (!inc) return { salesAgents: [], salesTeams: [] };
      return { salesAgents: inc.data.salesAgents, salesTeams: inc.data.salesTeams };
    },
    incentiveSales(state) {
      const userStore = useUserStore();
      const inc = this._current;
      if (!inc || !inc.data[userStore.currentSelection.incentives.display]?.champions) return { salesAgents: [], salesTeams: [] };
      const cloneFilter = (items) => items?.champions ? items.champions.map(c => ({ ...c })) : [];
      const salesAgents = cloneFilter(inc.data.salesAgents).filter(c => (c.salesEuroNew || 0) > 0 || ((c.salesAchievements?.bonus?.value || 0) > 0));
      const salesTeams = cloneFilter(inc.data.salesTeams).filter(c => (c.salesEuroNew || 0) > 0 || ((c.salesAchievements?.bonus?.value || 0) > 0) || ((c.salesAchievements?.cashBox?.value || 0) > 0));
      if (userStore.isSalesTeamManager) {
        const teamId = this._teamId;
        salesAgents.forEach(c => { c.highlightedPosition = Number(teamId(c)) === Number(userStore.currentSelection.salesTeam.id); });
      }
      return { salesAgents, salesTeams };
    },
    incentiveNoSalesNew(state) {
      const userStore = useUserStore();
      const inc = this._current;
      if (!inc || !inc.data[userStore.currentSelection.incentives.display]?.champions) return { salesAgents: [], salesTeams: [] };
      const clone = (items) => items?.champions ? items.champions.map(c => ({ ...c })) : [];
      const salesAgents = clone(inc.data.salesAgents).filter(c => (c.salesEuroNew || 0) === 0 && (c.salesEuro || 0) > 0 && ((c.salesAchievements?.bonus?.value || 0) === 0));
      const salesTeams = clone(inc.data.salesTeams).filter(c => (c.salesEuroNew || 0) === 0 && (c.salesEuro || 0) > 0 && ((c.salesAchievements?.bonus?.value || 0) === 0) && ((c.salesAchievements?.cashBox?.value || 0) === 0));
      if (userStore.isSalesTeamManager) {
        const teamId = this._teamId;
        salesAgents.forEach(c => c.highlightedPosition = Number(teamId(c)) === Number(userStore.currentSelection.salesTeam.id));
      }
      return { salesAgents, salesTeams };
    },
    incentiveNoSales(state) {
      const userStore = useUserStore();
      const inc = this._current;
      if (!inc || !inc.data[userStore.currentSelection.incentives.display]?.champions) return { salesAgents: [], salesTeams: [] };
      const clone = (items) => items?.champions ? items.champions.map(c => ({ ...c })) : [];
      const salesAgents = clone(inc.data.salesAgents).filter(c => (c.salesEuro || 0) === 0 && (c.salesEuroNew || 0) === 0 && ((c.salesAchievements?.bonus?.value || 0) === 0));
      const salesTeams = clone(inc.data.salesTeams).filter(c => (c.salesEuro || 0) === 0 && (c.salesEuroNew || 0) === 0 && ((c.salesAchievements?.bonus?.value || 0) === 0) && ((c.salesAchievements?.cashBox?.value || 0) === 0));
      if (userStore.isSalesTeamManager) {
        const teamId = this._teamId;
        salesAgents.forEach(c => c.highlightedPosition = Number(teamId(c)) === Number(userStore.currentSelection.salesTeam.id));
      }
      return { salesAgents, salesTeams };
    },
    // return the top three sales agents or sales teams
    topThree(state) {
      const userStore = useUserStore();
      const inc = this._current;
      const result = { salesAgents: [], salesTeams: [] };
      if (!inc) return result;
      // Podest Reihenfolge: 2., 1., 3. Platz für spezielles CSS Layout (mittlere Karte höher etc.)
      for (const display of ['salesAgents', 'salesTeams']) {
        const list = inc.data?.[display]?.champions || [];
        if (list.length > 1) result[display].push({ ...list[1] }); // second
        if (list.length > 0) result[display].push({ ...list[0] }); // first
        if (list.length > 2) result[display].push({ ...list[2] }); // third
      }
      // highlight clones only
  const teamId = this._teamId;
  result.salesTeams.forEach(c => { if (Number(teamId(c)) === Number(userStore.currentSelection.salesTeam?.id)) c.highlightedPosition = true; });
      result.salesAgents.forEach(c => { if (Number(c.idUser) === Number(userStore.id)) c.highlightedPosition = true; });
      return result;
    },
    // return the shortened list of those salesManagers/ salesTeams that did not make the top three for the widget
    preview(state) {
      const userStore = useUserStore();
      const inc = this._current;
      const preview = { salesAgents: [], salesTeams: [] };
      let previewStart = 0;
      let previewEnd = 2;
      if (!inc) return [];

      const dataSA = inc.data.salesAgents?.champions || [];
      const dataST = inc.data.salesTeams?.champions || [];

      if ((userStore.isManagement || userStore.isWinback) && userStore.currentSelection.scope !== 'salesAgent') {
        // ------------------------------------------------------------
        // MANAGEMENT / OBSERVER / WINBACK PREVIEW
        // Zeigt für beide Anzeigen (salesAgents & salesTeams) direkt die Plätze unter dem Podest.
        // Konkret: Plätze 4 bis 6 (Index 0..4 im Array weil topThree separat angezeigt wird und
        // hier die Reihenfolge 2,1,3,4,5 dargestellt wird). Fokus: Überblick über nächste Verfolger.
        // ------------------------------------------------------------
        for (const display of ['salesAgents', 'salesTeams']) {
          const list = display === 'salesAgents' ? dataSA : dataST;
            for (let i = 0; i <= 4; i++) {
              if (list[i]) preview[display].push({ ...list[i] });
            }
        }
      } else if (userStore.isSalesTeamManager && userStore.currentSelection.scope !== 'salesAgent') {
        // ------------------------------------------------------------
        // SALES TEAM MANAGER PREVIEW (BEREICH: EIGENES TEAM)
        // Ziel: Fokus auf eigene Team-Mitglieder unterhalb des Podests.
        // 1. Alle Agents des eigenen Teams (egal welche Position > 3) werden gesammelt
        // 2. Zusätzlich für salesTeams die Plätze 4 bis 6, um Team-Vergleich zu behalten
        // -> Eigene Teammitglieder erhalten highlightedPosition = true
        // ------------------------------------------------------------
        // collect own team members (not necessarily contiguous) below top 3
        {
          const teamId = this._teamId;
          for (const agent of dataSA) {
            if (Number(teamId(agent)) === Number(userStore.currentSelection.salesTeam.id)) {
              preview.salesAgents.push({ ...agent, highlightedPosition: true });
            }
          }
        }
        {
          const teamId = this._teamId;
          for (let i = 3; i <= Math.min(5, (dataST.length || 1) - 1); i++) {
            const team = dataST[i];
            if (!team) continue;
            preview.salesTeams.push({ ...team, highlightedPosition: Number(teamId(team)) === Number(userStore.currentSelection.salesTeam.id) });
          }
        }
      } else if (userStore.isSalesAgent || userStore.currentSelection.scope === 'salesAgent') {
        // ------------------------------------------------------------
        // SALES AGENT / AREAS PREVIEW
        // Zwei Fälle:
        //  a) Anzeige salesAgents: Fenster um die eigene Position (oder ausgewählten Agent) –
        //     wenn nicht Top 3 und nicht am Ende => 1 Platz darüber & darunter
        //     wenn am Ende => die letzten 3
        //  b) Anzeige salesTeams (Areas): Fenster um das eigene Team (index-1 .. index+1)
        // Anschließend Fallback-Erweiterung falls weniger als 3 Einträge sichtbar.
        // Alle kopierten Objekte: champion Highlight wird nur auf die jeweiligen Clone gesetzt.
        // ------------------------------------------------------------
        for (const display of ['salesAgents', 'salesTeams']) {
          let champion = null;
          let index = -1;
          if (display === 'salesAgents') {
            const champions = dataSA;
            if (userStore.currentSelection.scope === 'salesAgent') {
              if (userStore.isManagement || userStore.isSalesTeamManager || userStore.isWinback) {
                champion = champions.find(c => parseInt(c.idUser) === parseInt(userStore.currentSelection?.salesAgent?.idUser));
              } else {
                champion = champions.find(c => parseInt(c.idUser) === parseInt(userStore.id));
              }
            } else {
              champion = champions.find(c => parseInt(c.idUser) === parseInt(userStore.id));
            }
            if (!champion) continue;
            index = champions.findIndex(c => parseInt(c.idUser) === parseInt(champion.idUser));
            if (index > 2 && index < champions.length - 2) {
              previewStart = index - 1;
              previewEnd = index + 1;
            } else if (index >= champions.length - 2) {
              previewStart = champions.length - 2;
              previewEnd = index;
            }
          } else { // salesTeams
            const champions = dataST;
            const teamId = this._teamId;
            champion = champions.find(c => parseInt(teamId(c)) === parseInt(userStore.currentSelection.salesTeam.id));
            if (!champion) continue;
            const championTeamId = teamId(champion);
            index = champions.findIndex(c => parseInt(teamId(c)) === parseInt(championTeamId));
            previewStart = index - 1;
            previewEnd = index + 1;
          }
          previewStart = Math.max(0, previewStart);
          const champions = display === 'salesAgents' ? dataSA : dataST;
          previewEnd = Math.min(champions.length - 1, previewEnd);
          for (let i = previewStart; i <= previewEnd; i++) {
            const base = champions[i];
            if (!base) continue;
            const teamId = this._teamId;
            const isHighlight = (base.idUser && champion?.idUser && base.idUser === champion.idUser) || (teamId(base) && teamId(champion) && teamId(base) === teamId(champion));
            preview[display].push({ ...base, highlightedPosition: isHighlight });
          }
          if (preview[display].length < 3) {
            let need = 3 - preview[display].length;
            let i = previewEnd + 1;
            while (need > 0 && i < champions.length) {
              preview[display].push({ ...champions[i] });
              i++; need--;
            }
            i = previewStart - 1;
            while (need > 0 && i >= 0) {
              preview[display].unshift({ ...champions[i] });
              i--; need--;
            }
          }
        }
      }
      // original API of this getter returned only the active display array
      return preview[userStore.currentSelection.incentives.display];
    },
    /**
     * Centralized champion bonus selection based on current role & scope.
     * Returns { bonus, entitled, name, source, champion }.
     */
    championBonusInfo(state) {
      const userStore = useUserStore();
      const inc = this._current;
      const resultFallback = { bonus: 0, entitled: false, name: `${userStore.firstName} ${userStore.lastName}`, source: null, champion: null };
      if (!inc) return resultFallback;

      const scope = userStore.currentSelection.scope;
      const selectedSalesAgentId = userStore.currentSelection.salesAgent?.idUser || userStore.currentSelection.salesAgent?.id;
      const selectedSalesTeamId = userStore.currentSelection.salesTeam?.id;
      const teamId = this._teamId;

      // decide source list by role & scope
      let sourceKey = null;
      if (userStore.isSalesTeamManager && scope !== 'salesAgent') {
        sourceKey = 'salesTeams';
      } else if (userStore.isSalesTeamManager && scope === 'salesAgent') {
        sourceKey = 'salesAgents';
      } else if (userStore.isSalesAgent) {
        sourceKey = 'salesAgents';
      } else if (userStore.isManagement || userStore.isWinback) {
        if (scope === 'salesTeam') sourceKey = 'salesTeams';
        else if (scope === 'salesAgent') sourceKey = 'salesAgents';
        else sourceKey = null;
      } else {
        sourceKey = 'salesAgents';
      }

      const source = sourceKey ? (inc.data?.[sourceKey]?.champions || []) : [];
      let champion = null;
      if (sourceKey === 'salesAgents') {
        if (scope === 'salesAgent' && selectedSalesAgentId && selectedSalesAgentId !== -1) {
          champion = source.find(c => Number(c.idUser) === Number(selectedSalesAgentId));
        }
        if (!champion) champion = source.find(c => Number(c.idUser) === Number(userStore.id));
      } else if (sourceKey === 'salesTeams') {
        if (scope === 'salesTeam' && selectedSalesTeamId && selectedSalesTeamId !== -1) {
          champion = source.find(c => Number(teamId(c)) === Number(selectedSalesTeamId));
        }
        if (!champion) champion = source.find(c => c.highlightedPosition === true);
      }
      if (!champion) champion = source.find(c => c.highlightedPosition === true);
      if (!champion) champion = source[0] || null;

      if (!champion) {
        return {
          bonus: userStore.salesBonus || 0,
          entitled: userStore.salesBonusEntitled || false,
          name: `${userStore.firstName} ${userStore.lastName}`,
          source: sourceKey,
          champion: null,
        };
      }

      let bonus = champion.salesBonus;
      if ((!bonus || bonus === 0) && Array.isArray(champion.salesBonuses) && champion.salesBonuses.length) {
        const numericBonuses = champion.salesBonuses.filter(b => typeof b === 'number');
        if (numericBonuses.length) bonus = Math.max(...numericBonuses);
      }
      return {
        bonus: bonus || 0,
        entitled: !!champion.salesBonusEntitled,
        name: champion.salesAgentName || champion.salesTeamName || `${userStore.firstName} ${userStore.lastName}`,
        source: sourceKey,
        champion,
      };
    },
  },
  actions: {
    /** see if the incentives need to be fetched again, either because the data is not up to date or the display status has changed
    */
    async refresh() {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      const signature = `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}`;
      const incentiveExists = this.incentives.find((incentive) => incentive.signature === signature);
      if (incentiveExists && incentiveExists?.update > (Date.now() - (6 * 60 * 60 * 1000))) return incentiveExists;
      const taskList = [
        fetchFromBackend("incentives/v1/salesAgents", { id: userStore.id, filter: { idCompany: userStore.currentSelection.campaign.company.id, idCampaign: userStore.currentSelection.campaign.id, daysOffset: 3  } }),
        fetchFromBackend("incentives/v1/salesTeams", { id: userStore.id, filter: { idCompany: userStore.currentSelection.campaign.company.id, idCampaign: userStore.currentSelection.campaign.id, daysOffset: 3 } }),
        // fetchFromBackend("incentives/v1/salesAgents", { id: userStore.id, filter: { idCompany: userStore.currentSelection.campaign.company.id, idCampaign: userStore.currentSelection.campaign.id  } }),
        // fetchFromBackend("incentives/v1/salesTeams", { id: userStore.id, filter: { idCompany: userStore.currentSelection.campaign.company.id, idCampaign: userStore.currentSelection.campaign.id } }),
      ];
      userStore.currentSelection.incentives.isLoading = true;
      let responses = null;
      try {
        responses = await Promise.all(taskList);
        // notificationStore.addNotification('Incentives erfolgreich aktualisiert!', 'success');
      } catch (error) {
        userStore.currentSelection.incentives.isLoading = false;
        notificationStore.addNotification(`Incentives konnten nicht geladen werden! (${error?.message || error})`, 'error', 18);
        return false;
      }
      userStore.currentSelection.incentives.isLoading = false;
      if (!responses || !responses[0]?.incentive || !responses[1]?.incentive) {
        notificationStore.addNotification('Keine Incentives gefunden!', 'error', 18);
        return false;
      }
      if (incentiveExists) {
        incentiveExists.data = { "salesAgents": responses[0].incentive, "salesTeams": responses[1].incentive };
        incentiveExists.update = Date.now();
      } else {
        this.incentives.push({
          signature,
          data: {
            "salesAgents": responses[0].incentive,
            "salesTeams": responses[1].incentive
          },
          update: Date.now()
        });
      }
    },
  },
});
